'use client';

import { c as _c } from "react/compiler-runtime";
import { ChainId } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { DEPRECATED_VAULTS, type DeprecatedVault } from '@/config/deprecated-vaults';
import { EMBEDDERS } from '@/config/embedders';
import { useDebugOverridesSafe } from '@/contexts/DebugOverrides';
import { useMorphoLegacyContext } from '@/contexts/MorphoLegacy/MorphoLegacy.context';
import { useUtilityContext } from '@/contexts/UtilityContext/Utility.context';
import { useWeb3Context } from '@/contexts/Web3Context';
import { useEmbedder } from '@/hooks/useEmbedder';
import { useDeprecatedVaultPositions } from './useDeprecatedVaultPositions';
const NO_OVERRIDDEN_POSITIONS: readonly DeprecatedVault[] = [];
export interface NotificationsState {
  /** User holds legacy (pre-wrap) MORPHO tokens on Ethereum mainnet. */
  hasLegacyMorpho: boolean;
  /** Visitor is geolocated to the UK (FCA jurisdiction). Independent of wallet state. */
  isUkUser: boolean;
  /**
   * Copy for the partner-embed notice when the app is framed by an embedder that
   * defines `banner` (e.g. Mellow); `undefined` when not embedded by such a
   * partner. Surfaced as a status toast.
   */
  embedderBanner: string | undefined;
  /** Deprecated vaults the connected wallet still holds a redeemable position in. */
  deprecatedVaultPositions: readonly DeprecatedVault[];
}

/**
 * Detect status notifications surfaced in the toast stack. Debug overrides
 * short-circuit the real values so the debug panel can simulate each state.
 *
 * `hasLegacyMorpho` reads the holding from mainnet regardless of the connected chain, so
 * it surfaces for EOAs on any chain. A Safe can't switch chains, so it's suppressed unless
 * the Safe is already on mainnet (otherwise it could never execute the wrap).
 */
export const useNotifications = () => {
  const $ = _c(5);
  const {
    account
  } = useWeb3Context();
  const {
    legacyMorphoHolding
  } = useMorphoLegacyContext();
  const {
    countryCode
  } = useUtilityContext();
  const {
    overrides
  } = useDebugOverridesSafe();
  const embedder = useEmbedder();
  const deprecatedVaultPositions = useDeprecatedVaultPositions();
  const isSafeOffMainnet = !!account.isSafe && account.chain?.id !== ChainId.EthMainnet;
  const hasLegacyMorpho = overrides.hasLegacyMorpho ?? (!isSafeOffMainnet && !!(legacyMorphoHolding.value && legacyMorphoHolding.value > ZERO));
  const isUkUser = overrides.isUkUser ?? countryCode === "GB";
  const embedderBanner = overrides.showEmbedderBanner === undefined ? embedder?.banner : overrides.showEmbedderBanner ? embedder?.banner ?? EMBEDDERS.mellow.banner : undefined;
  const t0 = overrides.hasDeprecatedVaultPosition === undefined ? deprecatedVaultPositions : overrides.hasDeprecatedVaultPosition ? DEPRECATED_VAULTS : NO_OVERRIDDEN_POSITIONS;
  let t1;
  if ($[0] !== embedderBanner || $[1] !== hasLegacyMorpho || $[2] !== isUkUser || $[3] !== t0) {
    t1 = {
      hasLegacyMorpho,
      isUkUser,
      embedderBanner,
      deprecatedVaultPositions: t0
    };
    $[0] = embedderBanner;
    $[1] = hasLegacyMorpho;
    $[2] = isUkUser;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};